// GateList.tsx - Updated with namespaced CSS classes to avoid conflicts
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './GateManagement.css';
import '../EnterpriseTable.css'; 
import '../companyManagement/CompanyListOverrides.css';
import '../companyManagement/CompanyHelpPanel.css';
import '../companyManagement/ViewDetailsModal.css';
import { GateApiService } from './apiService';
import { GateViewDetailsModal } from './GateViewDetailsModal';
import { GateForm } from './GateForm';
import { ErrorMessageBanner } from '../companyManagement/ErrorMessageBanner';
import { Gate, Region, GateManagementProps, GateApprover, GateReviewer } from './types';

interface GateListProps extends GateManagementProps {
  gates: Gate[];
  regions: Region[];
  isLoading: boolean;
  isLoadingRegions: boolean;
  selectedRegion: Region | null;
  gateSearch: string;
  onRegionSelect: (region: Region) => void;
  onGateSearchChange: (search: string) => void;
  onEdit: (gate: Gate) => void;
  onCreateNew: () => void;
  onRefresh: () => void;
}

interface DropdownState {
  isOpen: boolean;
  gateId: string | null;
}

// Component to display user list with tooltips (simplified)
const UserList: React.FC<{ 
  users: (GateApprover | GateReviewer)[], 
  type: 'Approvers' | 'Reviewers',
  maxVisible?: number 
}> = ({ users, type, maxVisible = 2 }) => {
  if (!users || users.length === 0) {
    return <span className="no-regions-text">No {type.toLowerCase()}</span>;
  }

  const visibleUsers = users.slice(0, maxVisible);
  const remainingCount = users.length - maxVisible;

  return (
    <div className="regions-container">
      {visibleUsers.map((user) => (
        <div key={user.userId} className="region-tag">
          <span 
            className="region-chip default"
            title={`${user.userName} (${user.userEmail})`}
          >
            {user.userName}
          </span>
        </div>
      ))}
      {remainingCount > 0 && (
        <div className="region-tag">
          <span 
            className="region-chip default"
            title={users.slice(maxVisible).map(u => `${u.userName} (${u.userEmail})`).join('\n')}
            style={{ opacity: 0.7 }}
          >
            +{remainingCount} more
          </span>
        </div>
      )}
    </div>
  );
};

// =============================================
// Portal-based Dropdown Menu Component
// Renders outside the table DOM to avoid overflow clipping
// Styled to match OrganizationList company-dropdown-menu
// =============================================
const DropdownPortal: React.FC<{
  gate: Gate;
  position: { x: number; y: number };
  onViewDetails: (gate: Gate) => void;
  onEdit: (gate: Gate) => void;
  onDelete: (gate: Gate) => void;
  onClose: () => void;
}> = ({ gate, position, onViewDetails, onEdit, onDelete, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    // Use a short delay so the opening click doesn't immediately close it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Close on scroll (since it's fixed position)
  useEffect(() => {
    const handleScroll = () => onClose();
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [onClose]);

  // Close on Escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div ref={menuRef} className="gates-portal-dropdown-menu" style={{
      position: 'fixed',
      left: `${position.x}px`,
      top: `${position.y}px`,
    }}>
      <button onClick={() => onViewDetails(gate)} className="gates-portal-dropdown-item" type="button">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
        View Details
      </button>
      <button onClick={() => onEdit(gate)} className="gates-portal-dropdown-item" type="button">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        Edit
      </button>
      <div className="gates-portal-dropdown-divider"></div>
      <button onClick={() => onDelete(gate)} className="gates-portal-dropdown-item delete" type="button">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
        Delete
      </button>
    </div>,
    document.body
  );
};

export const GateList: React.FC<GateListProps> = ({
  gates,
  regions,
  isLoading,
  isLoadingRegions,
  selectedRegion,
  gateSearch,
  getAccessToken,
  onStatusUpdate,
  onRegionSelect,
  onGateSearchChange,
  onEdit,
  onCreateNew,
  onRefresh
}) => {
  const [showRegionSwitcher, setShowRegionSwitcher] = useState(false);
  const [regionSwitcherSearch, setRegionSwitcherSearch] = useState('');
  const [regionSwitcherPos, setRegionSwitcherPos] = useState<{x: number, y: number} | null>(null);
  const [dropdown, setDropdown] = useState<DropdownState>({ isOpen: false, gateId: null });
  const [apiService] = useState(() => new GateApiService(getAccessToken));
  const [dropdownPosition, setDropdownPosition] = useState<{x: number, y: number} | null>(null);
  const [activeGate, setActiveGate] = useState<Gate | null>(null);
  const [helpVisible, setHelpVisible] = useState(true);
  const [showActionHint, setShowActionHint] = useState(false);

  // Inline Create/Edit Form Modal State
  const [showFormModal, setShowFormModal] = useState(false);
  const [formEditingGate, setFormEditingGate] = useState<Gate | null>(null);

  // Error banner state (success messages are suppressed)
  const [errorBanner, setErrorBanner] = useState({ isVisible: false, message: '' });

  const closeErrorBanner = useCallback(() => {
    setErrorBanner({ isVisible: false, message: '' });
  }, []);

  // Local status handler: only show errors via banner, suppress success messages
  const handleLocalStatusUpdate = useCallback((status: { type: string | null; message: string }) => {
    if (status.type === 'error') {
      setErrorBanner({ isVisible: true, message: status.message });
    }
    // Success messages are intentionally not shown
  }, []);

  // View Details Modal State
  const [viewDetailsModal, setViewDetailsModal] = useState({
    isOpen: false,
    gate: null as Gate | null
  });

  const regionSwitcherRef = useRef<HTMLDivElement>(null);

  // Close region switcher on click outside
  useEffect(() => {
    const handleClickOutsideRegionSwitcher = (event: MouseEvent) => {
      if (regionSwitcherRef.current && !regionSwitcherRef.current.contains(event.target as Node)) {
        setShowRegionSwitcher(false);
        setRegionSwitcherSearch('');
        setRegionSwitcherPos(null);
      }
    };
    if (showRegionSwitcher) {
      document.addEventListener('mousedown', handleClickOutsideRegionSwitcher);
      return () => document.removeEventListener('mousedown', handleClickOutsideRegionSwitcher);
    }
  }, [showRegionSwitcher]);

  // Filter gates based on search
  const filteredGates = useMemo(() => {
    if (!gateSearch.trim()) {
      return gates;
    }

    const term = gateSearch.toLowerCase().trim();
    
    return gates.filter(gate => 
      gate.gateNumber.toLowerCase().includes(term) ||
      (gate.description && gate.description.toLowerCase().includes(term)) ||
      (gate.accessCodes && gate.accessCodes.toLowerCase().includes(term))
    );
  }, [gates, gateSearch]);

  // Toggle action dropdown - simplified since portal handles outside clicks
  const toggleDropdown = (gate: Gate, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (dropdown.isOpen && dropdown.gateId === gate.gateId.toString()) {
      // Close if same dropdown is clicked
      closeDropdown();
    } else {
      // Open new dropdown
      const rect = event.currentTarget.getBoundingClientRect();
      const dropdownWidth = 140;
      const viewportWidth = window.innerWidth;
      
      // Position to the left of button to avoid clipping
      let xPos = rect.left - dropdownWidth + rect.width;
      
      // Ensure dropdown doesn't go off left edge of screen
      if (xPos < 10) {
        xPos = rect.left;
      }
      
      // Ensure dropdown doesn't go off right edge of screen  
      if (xPos + dropdownWidth > viewportWidth - 10) {
        xPos = viewportWidth - dropdownWidth - 10;
      }

      setDropdown({ isOpen: true, gateId: gate.gateId.toString() });
      setDropdownPosition({ x: xPos, y: rect.bottom + 5 });
      setActiveGate(gate);
    }
  };

  const closeDropdown = () => {
    setDropdown({ isOpen: false, gateId: null });
    setDropdownPosition(null);
    setActiveGate(null);
  };

  // Clear search
  const clearSearch = () => {
    onGateSearchChange('');
  };

  // Auto-dismiss action hint
  useEffect(() => {
    if (!showActionHint) return;
    const timer = setTimeout(() => setShowActionHint(false), 6000);
    return () => clearTimeout(timer);
  }, [showActionHint]);

  useEffect(() => {
    if (dropdown.isOpen) setShowActionHint(false);
  }, [dropdown.isOpen]);

  // Handle delete gate
  const handleDelete = async (gate: Gate) => {
    closeDropdown();
    
    if (!window.confirm(`Are you sure you want to delete "${gate.gateNumber}"? This action cannot be undone.`)) {
      return;
    }

    const result = await apiService.deleteGate(gate);
    
    if (result.success) {
      onRefresh();
    } else {
      handleLocalStatusUpdate({
        type: 'error',
        message: result.message || 'Failed to delete gate'
      });
    }
  };

  // Handle edit
  const handleEdit = (gate: Gate) => {
    closeDropdown();
    setFormEditingGate(gate);
    setShowFormModal(true);
  };

  // Handle row click - opens view details
  const handleRowClick = (gate: Gate) => {
    setViewDetailsModal({
      isOpen: true,
      gate: gate
    });
  };

  // Handle view details
  const handleViewDetails = (gate: Gate) => {
    closeDropdown();
    setViewDetailsModal({
      isOpen: true,
      gate: gate
    });
  };

  // Handle close view details modal
  const handleCloseViewDetails = () => {
    setViewDetailsModal({
      isOpen: false,
      gate: null
    });
  };

  // Handle region selection from dropdown
  const handleRegionSelect = (region: Region) => {
    onRegionSelect(region);
  };

  // Filter regions for the inline switcher dropdown
  const switcherFilteredRegions = regions.filter(region =>
    region.regionName.toLowerCase().includes(regionSwitcherSearch.toLowerCase()) ||
    (region.regionCode && region.regionCode.toLowerCase().includes(regionSwitcherSearch.toLowerCase()))
  );

  // Handle switching region from the inline dropdown
  const handleSwitchRegion = (region: Region) => {
    setShowRegionSwitcher(false);
    setRegionSwitcherSearch('');
    setRegionSwitcherPos(null);
    onRegionSelect(region);
  };

  // Show region selection if no region is selected
  if (!selectedRegion) {
    return (
      <div className="company-management">
        {/* Error Message Banner */}
        <ErrorMessageBanner
          message={errorBanner.message}
          isVisible={errorBanner.isVisible}
          onClose={closeErrorBanner}
          autoCloseDelay={7000}
        />
        <div className={`cm-layout ${!helpVisible ? 'help-collapsed' : ''}`}>
          <div className="cm-main">
          <div className="companies-container">
            {/* Toolbar */}
            <div className="table-toolbar">
              <div className="toolbar-left">
                <span className="record-count">
                  {regions.length} region{regions.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            <div style={{ padding: '12px 20px 14px', fontSize: '13px', color: '#6b7280', borderBottom: '1px solid #f1f3f5' }}>
              Select a region to view and manage its gates
            </div>

            <div className="companies-table-container">
              <table className="enterprise-table">
                <thead>
                  <tr>
                    <th>Region</th>
                    <th>Code</th>
                    <th>Gates</th>
                    <th className="th-status">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingRegions ? (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center', padding: '40px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#6b7280', fontSize: '13px' }}>
                          <div className="spinner"></div>
                          Loading regions...
                        </div>
                      </td>
                    </tr>
                  ) : regions.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="no-results">
                        <div className="no-results-content">
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="10" r="3" />
                            <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z" />
                          </svg>
                          <p>No regions available</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    regions.map(region => (
                      <tr 
                        key={region.regionId} 
                        onClick={() => handleRegionSelect(region)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="td-name">
                          <div className="name-details">
                            <span className="org-name-text">{region.regionName}</span>
                          </div>
                        </td>
                        <td>
                          {region.regionCode ? (
                            <span className="region-chip default">{region.regionCode}</span>
                          ) : (
                            <span className="empty-field">&mdash;</span>
                          )}
                        </td>
                        <td style={{ color: '#374151', fontSize: '13px' }}>{region.gateCount || 0} gates</td>
                        <td className="td-status">
                          <span className={`status-pill ${region.isActive ? 'active' : 'blacklisted'}`}>
                            <span className="status-dot"></span>
                            {region.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          </div>

          {/* Right: Help Guide — animated collapse */}
          <div
            className="cm-help-right"
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              flexShrink: 0,
              position: 'sticky',
              top: 0,
              alignSelf: 'flex-start',
              width: helpVisible ? 310 : 44,
              padding: '28px 0 32px 0',
              transition: 'width 0.3s ease',
              overflow: 'hidden',
            }}
          >
            <div
              className="cm-help-content"
              style={{
                flex: helpVisible ? 1 : '0 0 0px',
                opacity: helpVisible ? 1 : 0,
                overflow: 'hidden',
                transition: 'opacity 0.25s ease, flex 0.3s ease',
                pointerEvents: helpVisible ? 'auto' : 'none',
                minWidth: 0,
                padding: helpVisible ? '0 16px 0 28px' : '0',
              }}
            >
              <div className="cm-help-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <p className="cm-help-title">Select a Region</p>
              <p className="cm-help-text">
                Click on a region row to view and manage the gates within it. Each region represents a physical area or facility.
              </p>
              <p className="cm-help-subtitle">Region Codes</p>
              <p className="cm-help-text">
                Short codes like DOH, RLC, LUS identify regions across the system and appear on gate passes.
              </p>
              <p className="cm-help-subtitle">Gate Counts</p>
              <p className="cm-help-text">
                The Gates column shows how many gates exist in each region. After selecting a region, you can add, edit, or remove gates.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setHelpVisible(!helpVisible)}
              aria-label={helpVisible ? 'Hide Guide' : 'Show Guide'}
              title={helpVisible ? 'Hide Guide' : 'Show Guide'}
              style={helpVisible ? {
                display: 'flex', alignItems: 'center', gap: 6,
                writingMode: 'vertical-rl', textOrientation: 'mixed',
                background: '#f5f5f5', border: '1px solid #e0e0e0', borderRight: 'none',
                borderRadius: '6px 0 0 6px', padding: '12px 7px',
                fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer',
                letterSpacing: '0.02em', flexShrink: 0, alignSelf: 'flex-start',
                marginTop: 36, zIndex: 2, transition: 'background 0.15s ease',
              } : {
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 34, height: 34, borderRadius: '50%',
                border: '1.5px solid #d4d4d4', background: '#ffffff', cursor: 'pointer',
                flexShrink: 0, margin: '6px auto 0 auto', padding: 0,
                transition: 'background 0.15s ease, border-color 0.15s ease',
              }}
              onMouseEnter={(e) => {
                if (helpVisible) { e.currentTarget.style.background = '#ebebeb'; }
                else { e.currentTarget.style.borderColor = '#F3694A'; e.currentTarget.style.background = '#fef7f5'; }
              }}
              onMouseLeave={(e) => {
                if (helpVisible) { e.currentTarget.style.background = '#f5f5f5'; }
                else { e.currentTarget.style.borderColor = '#d4d4d4'; e.currentTarget.style.background = '#ffffff'; }
              }}
            >
              {helpVisible ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: 'rotate(90deg)' }}>
                    <circle cx="12" cy="12" r="10" /><path d="M15 9l-6 3 6 3" />
                  </svg>
                  <span>Hide Guide</span>
                </>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#848d9a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main gate list view with selected region
  return (
    <div className="company-management">
      {/* Error Message Banner */}
      <ErrorMessageBanner
        message={errorBanner.message}
        isVisible={errorBanner.isVisible}
        onClose={closeErrorBanner}
        autoCloseDelay={7000}
      />
      <div className={`cm-layout ${!helpVisible ? 'help-collapsed' : ''}`}>
        <div className="cm-main">
      <div className="companies-container">
        {/* Combined toolbar with region selector and create button */}
        <div style={{ 
          padding: '12px 16px', 
          borderBottom: '1px solid #e8e8e8',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px'
        }}>
          {/* Top row: Region selector + Create button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Region Selector */}
            <div style={{ position: 'relative' }} ref={regionSwitcherRef}>
              <button
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setRegionSwitcherPos({ x: rect.left, y: rect.bottom + 4 });
                  setShowRegionSwitcher(!showRegionSwitcher);
                  setRegionSwitcherSearch('');
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '6px 12px', background: '#fff', border: '1px solid #d1d5db',
                  borderRadius: '6px', cursor: 'pointer', fontSize: '14px', color: '#374151'
                }}
              >
                <span style={{ color: '#6b7280', fontSize: '13px' }}>Region:</span>
                <span style={{ fontWeight: 600 }}>{selectedRegion.regionName}</span>
                {selectedRegion.regionCode && (
                  <span className="region-chip default" style={{ fontSize: '11px', padding: '1px 6px' }}>
                    {selectedRegion.regionCode}
                  </span>
                )}
                <span style={{ color: '#9ca3af', fontSize: '12px', marginLeft: '4px' }}>▼</span>
              </button>
              
              {/* Region Switcher Dropdown */}
              {showRegionSwitcher && regionSwitcherPos && (
                <div style={{
                  position: 'fixed',
                  left: regionSwitcherPos.x,
                  top: regionSwitcherPos.y,
                  width: '280px',
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  zIndex: 9999,
                  overflow: 'hidden'
                }}>
                  <div style={{ padding: '8px', borderBottom: '1px solid #f3f4f6' }}>
                    <input
                      type="text"
                      placeholder="Search regions..."
                      value={regionSwitcherSearch}
                      onChange={(e) => setRegionSwitcherSearch(e.target.value)}
                      autoFocus
                      style={{
                        width: '100%', padding: '6px 10px', border: '1px solid #e5e7eb',
                        borderRadius: '4px', fontSize: '13px', outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                    {switcherFilteredRegions.length > 0 ? (
                      switcherFilteredRegions.map(region => (
                        <div
                          key={region.regionId}
                          onClick={() => handleSwitchRegion(region)}
                          style={{
                            padding: '10px 12px', cursor: 'pointer', fontSize: '13px',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            background: region.regionId === selectedRegion.regionId ? '#f9fafb' : '#fff',
                            borderBottom: '1px solid #f9fafb',
                            transition: 'background 0.1s'
                          }}
                          onMouseEnter={(e) => {
                            if (region.regionId !== selectedRegion.regionId) {
                              e.currentTarget.style.background = '#f9fafb';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = region.regionId === selectedRegion.regionId ? '#f9fafb' : '#fff';
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontWeight: region.regionId === selectedRegion.regionId ? 600 : 400, color: '#374151' }}>
                              {region.regionName}
                            </span>
                            {region.regionCode && (
                              <span style={{
                                fontSize: '11px', padding: '1px 6px', background: '#f3f4f6',
                                color: '#374151', borderRadius: '4px', fontWeight: 500,
                                border: '1px solid #e5e7eb'
                              }}>
                                {region.regionCode}
                              </span>
                            )}
                            <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                              ({region.gateCount || 0})
                            </span>
                          </div>
                          <div style={{
                            width: '8px', height: '8px', borderRadius: '50%',
                            background: region.isActive ? '#F3694A' : '#d1d5db',
                            flexShrink: 0
                          }} />
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                        No regions match "{regionSwitcherSearch}"
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right side: Create Gate button */}
            <button onClick={() => { setFormEditingGate(null); setShowFormModal(true); }} className="create-company-btn">
              <span>Create Gate</span>
              <span>+</span>
            </button>
          </div>

          {/* Bottom row: Gate search + stats */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
            <div className="search-field" style={{ flex: 1 }}>
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="Find gates..."
                value={gateSearch}
                onChange={(e) => onGateSearchChange(e.target.value)}
                className="search-input"
              />
              {gateSearch && (
                <button 
                  onClick={clearSearch}
                  className="clear-search-btn"
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' }}>
              {gates.length} {gates.length === 1 ? 'gate' : 'gates'} total
              {gateSearch && filteredGates.length !== gates.length && (
                <span> · {filteredGates.length} shown</span>
              )}
            </div>
          </div>
        </div>

        <div className="companies-table-container">
          <table className="enterprise-table">
            <thead>
              <tr>
                <th>Gate Number</th>
                <th>Access Codes</th>
                <th>Approvers</th>
                <th>Reviewers</th>
                <th className="th-status">Status</th>
                <th className="th-actions"></th>
              </tr>
            </thead>
            <tbody>
              {filteredGates.length === 0 ? (
                <tr>
                  <td colSpan={6} className="no-results">
                    {gates.length === 0 && !gateSearch ? (
                      /* No gates exist in this region at all */
                      <div className="no-results-content">
                        <span className="no-results-icon">
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="7" height="7" rx="1" />
                            <rect x="14" y="3" width="7" height="7" rx="1" />
                            <rect x="3" y="14" width="7" height="7" rx="1" />
                            <rect x="14" y="14" width="7" height="7" rx="1" />
                          </svg>
                        </span>
                        <p style={{ fontWeight: 500, color: '#374151', marginBottom: '4px' }}>
                          No gates in {selectedRegion?.regionName || 'this region'} yet
                        </p>
                        <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 12px' }}>
                          Create your first gate to define access points and assign approvers.
                        </p>
                        <button
                          onClick={() => { setFormEditingGate(null); setShowFormModal(true); }}
                          className="clear-search-link"
                          style={{ fontWeight: 500 }}
                        >
                          Create Gate +
                        </button>
                      </div>
                    ) : (
                      /* Search/filter returned no matches */
                      <div className="no-results-content">
                        <span className="no-results-icon">
                          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                          </svg>
                        </span>
                        <p>No gates found matching "{gateSearch}"</p>
                        <button onClick={clearSearch} className="clear-search-link">
                          Clear search to see all gates
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ) : (
                filteredGates.map((gate, rowIndex) => (
                  <tr
                    key={gate.gateId}
                    className="clickable-row"
                    onClick={() => handleRowClick(gate)}
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleRowClick(gate); }}
                  >
                    <td className="td-name">
                      <div className="name-details">
                        <span className="org-name-text">{gate.gateNumber}</span>
                        {gate.description && (
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            {gate.description.length > 50 ? 
                              gate.description.substring(0, 50) + '...' : 
                              gate.description
                            }
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      {gate.accessCodes ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '180px' }}>
                          {gate.accessCodes.split(',').map((code: string, idx: number) => (
                            <span key={idx} className="region-chip default" style={{ flexShrink: 0 }}>
                              {code.trim()}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="empty-field">&mdash;</span>
                      )}
                    </td>
                    <td>
                      <UserList users={gate.approvers || []} type="Approvers" maxVisible={2} />
                    </td>
                    <td>
                      <UserList users={gate.reviewers || []} type="Reviewers" maxVisible={2} />
                    </td>
                    <td className="td-status">
                      <span className={`status-pill ${gate.isActive ? 'active' : 'blacklisted'}`}>
                        <span className="status-dot"></span>
                        {gate.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="td-actions" onClick={(e) => e.stopPropagation()}>
                      <div className="gates-action-dropdown">
                        {rowIndex === 0 && showActionHint && (
                          <div className="action-hint-tooltip" onClick={() => setShowActionHint(false)}>
                            <span>Click to view, edit, or delete this gate</span>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </div>
                        )}
                        <button
                          onClick={(e) => toggleDropdown(gate, e)}
                          className={`gates-action-trigger ${rowIndex === 0 && showActionHint ? 'hint-pulse' : ''}`}
                          title="More actions"
                          type="button"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="12" cy="5" r="2" />
                            <circle cx="12" cy="12" r="2" />
                            <circle cx="12" cy="19" r="2" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
        </div>

        {/* Right: Help Guide — animated collapse */}
        <div
          className="cm-help-right"
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            flexShrink: 0,
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            width: helpVisible ? 310 : 44,
            padding: '28px 0 32px 0',
            transition: 'width 0.3s ease',
            overflow: 'hidden',
          }}
        >
          <div
            className="cm-help-content"
            style={{
              flex: helpVisible ? 1 : '0 0 0px',
              opacity: helpVisible ? 1 : 0,
              overflow: 'hidden',
              transition: 'opacity 0.25s ease, flex 0.3s ease',
              pointerEvents: helpVisible ? 'auto' : 'none',
              minWidth: 0,
              padding: helpVisible ? '0 16px 0 28px' : '0',
            }}
          >
            <div className="cm-help-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <p className="cm-help-title">Gate (Asset) Management</p>
            <p className="cm-help-text">Manage physical gates and access points for the selected region. Each gate can have its own approvers and reviewers.</p>
            <p className="cm-help-subtitle">Access Codes</p>
            <p className="cm-help-text">Access codes are used to identify gates during security checks. Multiple codes can be assigned per gate.</p>
            <p className="cm-help-subtitle">Approvers &amp; Reviewers</p>
            <p className="cm-help-text">Gate-level approvers handle the final approval step. Reviewers validate requests before they reach gate approvers.</p>
            <p className="cm-help-subtitle">Quick Actions</p>
            <p className="cm-help-text">Click any row to view full details with action buttons inside. Or use the three-dot menu for quick access to edit or delete a gate.</p>
          </div>

          <button
            type="button"
            onClick={() => setHelpVisible(!helpVisible)}
            aria-label={helpVisible ? 'Hide Guide' : 'Show Guide'}
            title={helpVisible ? 'Hide Guide' : 'Show Guide'}
            style={helpVisible ? {
              display: 'flex', alignItems: 'center', gap: 6,
              writingMode: 'vertical-rl', textOrientation: 'mixed',
              background: '#f5f5f5', border: '1px solid #e0e0e0', borderRight: 'none',
              borderRadius: '6px 0 0 6px', padding: '12px 7px',
              fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer',
              letterSpacing: '0.02em', flexShrink: 0, alignSelf: 'flex-start',
              marginTop: 36, zIndex: 2, transition: 'background 0.15s ease',
            } : {
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 34, height: 34, borderRadius: '50%',
              border: '1.5px solid #d4d4d4', background: '#ffffff', cursor: 'pointer',
              flexShrink: 0, margin: '6px auto 0 auto', padding: 0,
              transition: 'background 0.15s ease, border-color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              if (helpVisible) { e.currentTarget.style.background = '#ebebeb'; }
              else { e.currentTarget.style.borderColor = '#F3694A'; e.currentTarget.style.background = '#fef7f5'; }
            }}
            onMouseLeave={(e) => {
              if (helpVisible) { e.currentTarget.style.background = '#f5f5f5'; }
              else { e.currentTarget.style.borderColor = '#d4d4d4'; e.currentTarget.style.background = '#ffffff'; }
            }}
          >
            {helpVisible ? (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: 'rotate(90deg)' }}>
                  <circle cx="12" cy="12" r="10" /><path d="M15 9l-6 3 6 3" />
                </svg>
                <span>Hide Guide</span>
              </>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#848d9a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Portal-based Dropdown - renders at document.body level */}
      {dropdown.isOpen && activeGate && dropdownPosition && (
        <DropdownPortal
          gate={activeGate}
          position={dropdownPosition}
          onViewDetails={handleViewDetails}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onClose={closeDropdown}
        />
      )}

    {/* Gate View Details Modal */}
{viewDetailsModal.gate && (
  <GateViewDetailsModal
    gate={{
      ...viewDetailsModal.gate,
      locationName: viewDetailsModal.gate.locationName || selectedRegion?.regionName || ''
    }}
    isOpen={viewDetailsModal.isOpen}
    onClose={handleCloseViewDetails}
    onEdit={(gate) => {
      handleCloseViewDetails();
      setFormEditingGate(gate);
      setShowFormModal(true);
    }}
    onDelete={(gate) => {
      handleCloseViewDetails();
      handleDelete(gate);
    }}
  />
)}

      {/* Inline Create/Edit Gate Form Modal — overlays the list */}
      {showFormModal && (
        <GateForm
          editingGate={formEditingGate}
          regions={regions}
          selectedRegion={selectedRegion}
          getAccessToken={getAccessToken}
          onStatusUpdate={handleLocalStatusUpdate}
          onBack={() => { setShowFormModal(false); setFormEditingGate(null); }}
          onSuccess={() => { setShowFormModal(false); setFormEditingGate(null); onRefresh(); }}
        />
      )}
    </div>
  );
};